/**
 * Task Service — CRUD de tareas clínicas via API.
 *
 * Endpoints: /api/ClinicalOperations/tasks*
 * Backend: ops.TAB_TASKS en DB_MCM_SANTEPLUS
 *
 * Migrado de localStorage a API (2026-04-09).
 */

import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/ClinicalOperations`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// ── CRUD ─────────────────────────────────────────────────────────

/**
 * Crea una nueva tarea clínica.
 *
 * @param {Object} taskData
 * @param {string} taskData.title - Título de la tarea (requerido, max 500 chars)
 * @param {string} [taskData.description] - Descripción detallada
 * @param {number} taskData.categoryId - ID de categoría (grupo task_category en TAB_STATUSES)
 * @param {number} taskData.priorityId - ID de prioridad (grupo task_priority en TAB_STATUSES)
 * @param {string} [taskData.dueDate] - Fecha de vencimiento (YYYY-MM-DD)
 * @param {string|number} [taskData.cipId] - CIP ID del paciente
 * @param {string} [taskData.patientName] - Nombre del paciente
 * @param {number} [taskData.locationId] - ID de la location
 * @param {string} [taskData.relatedType] - Contexto de origen
 * @param {number} [taskData.assigneeId] - ID del empleado asignado
 * @param {number} taskData.creatorId - ID del empleado creador
 *
 * Ejemplo de llamada:
 * POST /api/ClinicalOperations/tasks
 * Body: { "title": "Follow-up call", "categoryId": 33, "priorityId": 30, "creatorId": 42 }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Task created successfully", "taskId": 88 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Title is required" }
 * { "result": 1, "message": "Invalid category" }
 * { "result": 1, "message": "Invalid priority" }
 * { "result": 1, "message": "Creator employee not found" }
 */
// Normaliza un ID de empleado a int (>0) o null. Evita que el JWT/storage
// entregue strings ("89") y rompan el binding del backend.
const toEmployeeIdOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const createTask = async (taskData) => {
  const creatorId  = toEmployeeIdOrNull(taskData.creatorId);
  const assigneeId = toEmployeeIdOrNull(taskData.assigneeId);

  if (creatorId === null) {
    console.error("[Main/taskService.createTask] creatorId inválido:", taskData.creatorId);
    throw new Error("creatorId is required to create a task.");
  }

  const payload = {
    title: taskData.title || "",
    description: taskData.description || null,
    categoryId: taskData.categoryId,
    priorityId: taskData.priorityId,
    dueDate: taskData.dueDate || null,
    cipId: taskData.cipId ? String(taskData.cipId) : null,
    patientName: taskData.patientName || null,
    locationId: taskData.locationId || null,
    relatedType: taskData.relatedType || null,
    assigneeId,
    creatorId,
  };
  const response = await axios.post(`${API_URL}/tasks`, payload, authHeaders());
  return response.data;
};

/**
 * Obtiene tareas con filtros opcionales.
 *
 * @param {Object} [filters]
 * @param {number} [filters.locationId] - Filtrar por location
 * @param {number} [filters.status] - Filtrar por status ID
 * @param {number} [filters.priorityId] - Filtrar por prioridad
 * @param {number} [filters.categoryId] - Filtrar por categoría
 * @param {number} [filters.assigneeId] - Filtrar por empleado asignado
 * @param {number} [filters.creatorId] - Filtrar por empleado creador
 * @param {string} [filters.cipId] - Filtrar por paciente
 * @param {string} [filters.relatedType] - Filtrar por contexto
 *
 * Ejemplo de llamada:
 * GET /api/ClinicalOperations/tasks?locationId=2&status=26
 *
 * Respuesta exitosa (200): Array de tareas con campos denormalizados
 * (categoryKey, priorityKey, statusKey, assigneeName, creatorName, etc.)
 */
export const getTasks = async (filters = {}) => {
  const params = {};
  if (filters.locationId) params.locationId = filters.locationId;
  if (filters.status) params.status = filters.status;
  if (filters.priorityId) params.priorityId = filters.priorityId;
  if (filters.categoryId) params.categoryId = filters.categoryId;
  if (filters.assigneeId) params.assigneeId = filters.assigneeId;
  if (filters.creatorId) params.creatorId = filters.creatorId;
  if (filters.cipId) params.cipId = String(filters.cipId);
  if (filters.relatedType) params.relatedType = filters.relatedType;

  const response = await axios.get(`${API_URL}/tasks`, { ...authHeaders(), params });
  return response.data;
};

/**
 * Obtiene estadísticas de tareas.
 *
 * @param {Object} [filters]
 * @param {number} [filters.locationId] - Filtrar por location
 * @param {number} [filters.assigneeId] - Filtrar por empleado asignado
 * @param {number} [filters.creatorId] - Filtrar por empleado creador
 *
 * Ejemplo de llamada:
 * GET /api/ClinicalOperations/tasks/stats?locationId=2
 *
 * Respuesta exitosa (200):
 * { "totalTasks": 45, "totalPending": 18, "totalInProgress": 10,
 *   "totalCompleted": 14, "totalCancelled": 3, "totalHigh": 8,
 *   "totalMedium": 22, "totalLow": 15, "totalOverdue": 5, "totalActive": 28 }
 */
export const getTaskStats = async (filters = {}) => {
  const params = {};
  if (filters.locationId) params.locationId = filters.locationId;
  if (filters.assigneeId) params.assigneeId = filters.assigneeId;
  if (filters.creatorId) params.creatorId = filters.creatorId;

  const response = await axios.get(`${API_URL}/tasks/stats`, { ...authHeaders(), params });
  return response.data;
};

/**
 * Marca una tarea como completada.
 *
 * @param {number} taskId - ID de la tarea
 * @param {number} [employeeId] - Empleado que completa
 *
 * Ejemplo: PATCH /api/ClinicalOperations/tasks/88/complete
 * Respuesta: { "result": 0, "message": "Task status updated successfully" }
 */
export const completeTask = async (taskId, employeeId = null) => {
  const response = await axios.patch(
    `${API_URL}/tasks/${taskId}/complete`,
    { employeeId },
    authHeaders()
  );
  return response.data;
};

/**
 * Actualiza el status de una tarea.
 *
 * @param {number} taskId - ID de la tarea
 * @param {number} newStatus - Nuevo status ID (26=pending, 27=in_progress, 28=completed, 29=cancelled)
 * @param {number} [employeeId] - Empleado que realiza el cambio
 *
 * Ejemplo: PATCH /api/ClinicalOperations/tasks/88/status
 * Body: { "newStatus": 27, "employeeId": 42 }
 * Respuesta: { "result": 0, "message": "Task status updated successfully" }
 */
export const updateTaskStatus = async (taskId, newStatus, employeeId = null) => {
  const response = await axios.patch(
    `${API_URL}/tasks/${taskId}/status`,
    { newStatus, employeeId },
    authHeaders()
  );
  return response.data;
};

/**
 * Elimina una tarea (hard delete). Solo el creador puede eliminar.
 *
 * @param {number} taskId - ID de la tarea
 * @param {number} employeeId - ID del empleado que solicita
 *
 * Ejemplo: DELETE /api/ClinicalOperations/tasks/88
 * Body: { "employeeId": 42 }
 * Respuesta: { "result": 0, "message": "Task deleted successfully" }
 */
export const deleteTask = async (taskId, employeeId) => {
  const response = await axios.delete(`${API_URL}/tasks/${taskId}`, {
    ...authHeaders(),
    data: { employeeId },
  });
  return response.data;
};
